// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '../../i18n/LanguageContext';

const DeleteModalPartner = ({ partnerID, partnerName, handleDelete }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const closeModal = useCallback(() => {
    if (!isDeleting) setIsModalOpen(false);
  }, [isDeleting]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, closeModal]);

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    const deleted = await handleDelete(partnerID);
    setIsDeleting(false);

    if (deleted !== false) setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        aria-label={t('partners.delete.open')}
        title={t('partners.delete.open')}
      >
        <TrashIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-8 backdrop-blur-[2px]"
          onMouseDown={closeModal}
          role="presentation"
        >
          <section
            className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
            onMouseDown={(event) => event.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-partner-title"
            aria-describedby="delete-partner-description"
          >
            <div className="flex items-start gap-4 px-6 pb-5 pt-6 sm:px-7">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-100">
                <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
              </div>

              <div className="min-w-0 flex-1 pt-0.5">
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-red-600">
                  {t('partners.delete.warning')}
                </p>
                <h2 id="delete-partner-title" className="text-xl font-extrabold text-gray-950">
                  {t('partners.delete.title')}
                </h2>
                <p id="delete-partner-description" className="mt-2 text-sm leading-6 text-gray-600">
                  {t('partners.delete.description')}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={isDeleting}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl disabled:opacity-50"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="mx-6 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 sm:mx-7">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('partners.delete.partner')}
              </span>
              <span className="mt-1 block truncate text-sm font-bold text-gray-900">
                {partnerName || partnerID}
              </span>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-5 sm:flex-row sm:justify-end sm:px-7">
              <button
                type="button"
                onClick={closeModal}
                disabled={isDeleting}
                className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-65"
              >
                {isDeleting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                ) : (
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                )}
                {isDeleting ? t('partners.delete.deleting') : t('partners.delete.confirm')}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default DeleteModalPartner;
